import { SensorData } from '../model/sensorData';
import { DateUtils } from './dateUtils';
import { EssayTypeEnum, EssayUtils } from './essayUtils';
import { SensorGraphUtils } from './sensorGraphUtils';

export interface SliderValues {
  value: number;
  highValue: number;
}

/** Offset de la zona en un instante (minutos, signo de Date.getTimezoneOffset). */
export type OffsetAt = (ms: number) => number;

export interface DateRange {
  start: Date;
  end: Date;
}

/**
 * Logica de la franja del dialogo de eventos (slider + campos de texto). QLOD-52.
 */
export class EventRangeUtils {

  static readonly ONE_HOUR_MS = 60 * 60 * 1000;

  /** Ensayos cuyo evento dura lo que dice el tiempo de ensayo declarado (decision de Calidad, QLOD-52). */
  static readonly TYPES_WITH_DECLARED_TIME: number[] = [EssayTypeEnum.ENSAYO_APERTURA_DE_PUERTA, EssayTypeEnum.ENSAYO_CORTE_ELECTRICO];

  /**
   * Duracion en ms de un tiempo de ensayo "dias;horas;minutos" (o separado por comas, como lo
   * acepta la grafica principal). Estricta: enteros no negativos y un solo separador. `absent` si no hay o no suma
   * nada; `invalid` si hay algo que no se entiende (p. ej. "x;;5"), que NO se usa para calcular fechas.
   */
  static rehearsalTimeMs(rehearsalTime: string): { ms: number, absent: boolean, invalid: boolean } {
    if (rehearsalTime == null || rehearsalTime.trim() === '') {
      return { ms: null, absent: true, invalid: false };
    }

    // Un solo separador por valor (";" o ","), de uno a tres enteros no negativos.
    const m = /^\s*(\d+)\s*(?:(;|,)\s*(\d+)\s*(?:\2\s*(\d+)\s*)?)?$/.exec(rehearsalTime);
    if (m == null) {
      return { ms: null, absent: false, invalid: true };
    }

    const [days, hours, minutes] = [m[1], m[3], m[4]].map(v => (v != null ? Number(v) : 0));
    const ms = ((days * 24 + hours) * 60 + minutes) * 60 * 1000;

    if (!Number.isSafeInteger(ms)) {
      return { ms: null, absent: false, invalid: true };
    }

    return ms > 0 ? { ms, absent: false, invalid: false } : { ms: null, absent: true, invalid: false };
  }

  /**
   * Franja por defecto de un evento nuevo, desde el primer dato. Apertura de puerta y corte
   * electrico: el tiempo de ensayo declarado en la ejecucion. El resto (distribucion en vacio,
   * con media carga, con carga completa, con carga) o sin tiempo declarado: 1 h, y el tecnico
   * la ajusta. Un tiempo declarado invalido no se usa: 1 h y `invalidDeclared` para avisar.
   * Si pasa del ultimo dato se recorta a el, para que slider y campos coincidan.
   */
  static defaultRange(firstDate: Date, lastDate: Date, rehearsalTime: string, idEssayType: number): DateRange & { invalidDeclared: boolean } {
    const usesDeclared = EventRangeUtils.TYPES_WITH_DECLARED_TIME.includes(idEssayType);
    const declared = usesDeclared ? EventRangeUtils.rehearsalTimeMs(rehearsalTime) : null;

    let end = firstDate.getTime() + (declared?.ms ?? EventRangeUtils.ONE_HOUR_MS);

    if (lastDate != null && end > lastDate.getTime()) {
      end = Math.max(lastDate.getTime(), firstDate.getTime());
    }

    return { start: new Date(firstDate.getTime()), end: new Date(end), invalidDeclared: declared?.invalid === true };
  }

  /**
   * Comportamiento del slider. Con draggableRangeOnly solo se podia arrastrar la franja entera,
   * no estirar ni encoger sus extremos, y obligaba a teclear las horas a mano.
   */
  static readonly SLIDER_DRAG = { draggableRange: true, draggableRangeOnly: false };

  /**
   * Copia de la serie ordenada por fecha, con la fecha como Date y solo con las filas que se pintan
   * (las que tienen equipmentName). calculateNearDate necesita la serie ordenada, y la grafica
   * con lineas tambien. No toca ni reordena el array del llamante.
   */
  static sortedByDate(data: SensorData[]): SensorData[] {
    return (data ?? []).filter(i => i.date != null && i.equipmentName)
      .map(i => Object.assign(Object.create(Object.getPrototypeOf(i)), i, { date: DateUtils.anyToDate(i.date) }) as SensorData)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  /**
   * Extremos que ha movido el usuario al soltar el slider, ajustados al dato mas cercano; null en
   * el extremo que no se ha movido, que conserva lo que diga su campo (p. ej. una hora tecleada
   * con segundos, o fuera de la escala del slider). Se compara con el valor del slider al empezar
   * a arrastrar, no con la fecha del campo, porque ngx-slider recorta a floor/ceil.
   */
  static movedEnds(before: SliderValues, after: SliderValues, sorted: SensorData[]): { start: Date, end: Date } {
    const moved = (b: number, a: number) => b == null || b !== a;

    return {
      start: moved(before?.value, after.value) ? EssayUtils.calculateNearDate(new Date(after.value), sorted) : null,
      end: moved(before?.highValue, after.highValue) ? EssayUtils.calculateNearDate(new Date(after.highValue), sorted) : null
    };
  }

  /**
   * La grafica del dialogo trabaja en milisegundos epoch sobre un eje lineal, no en fechas de Plotly:
   * Plotly maneja las fechas como hora local sin zona y en los cambios de hora (hora repetida en
   * otono, hora inexistente en primavera, transiciones de 30 min) dos instantes comparten etiqueta
   * o una etiqueta no tiene instante. En epoch la coordenada es monotona y no hay ambiguedad; solo
   * las etiquetas se pintan en hora local. Devuelve el epoch de un valor de Plotly o null si no es
   * un numero finito.
   */
  static shapeEpoch(value: unknown): number {
    const n = typeof value === 'number' ? value : (typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN);

    return Number.isFinite(n) ? n : null;
  }

  /**
   * Fecha local "dd/MM/yyyy HH:mm:ss", identica a la de los campos (SensorGraphUtils.formatFieldDate)
   * pero sin luxon: se hace una por punto de la grafica (medido: 300.000 puntos, 1,5 s con luxon y
   * 0,3 s asi).
   */
  static formatLocal(ms: number): string {
    const d = new Date(ms);
    const p = (n: number, w = 2) => n.toString().padStart(w, '0');

    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${p(d.getFullYear(), 4)} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  }

  /** Marcas del eje X en hora local ("dd/MM/yyyy HH:mm:ss", como los campos), repartidas entre min y max. */
  static axisTicks(min: number, max: number, count = 8): { tickvals: number[], ticktext: string[] } {
    if (min == null || max == null || !(max > min) || count < 2) {
      return { tickvals: [], ticktext: [] };
    }

    const tickvals = Array.from({ length: count }, (_, i) => Math.round(min + (max - min) * i / (count - 1)));

    return { tickvals, ticktext: tickvals.map(v => EventRangeUtils.formatLocal(v)) };
  }

  /**
   * Extremos movidos al arrastrar o estirar la franja sobre la grafica (shape de Plotly, en epoch),
   * con el mismo criterio que el slider: solo se ajusta al dato mas cercano el extremo que se ha
   * movido. `before` son los extremos que tenia la franja. Si se cruzan los bordes se reordenan.
   * Al estirar un borde, Plotly tambien mueve el otro por debajo del pixel (medido: 12 ms con 1 h en
   * 1000 px): `toleranceMs` (un pixel en ms) evita tomarlo por un movimiento y reajustarlo.
   */
  static movedEndsFromShape(before: SliderValues, x0: unknown, x1: unknown,
    sorted: SensorData[], toleranceMs = 1): { start: Date, end: Date } {
    const a = EventRangeUtils.shapeEpoch(x0);
    const b = EventRangeUtils.shapeEpoch(x1);

    if (a == null || b == null) {
      return { start: null, end: null };
    }

    const [lo, hi] = a <= b ? [a, b] : [b, a];
    const tol = Math.max(1, toleranceMs);
    const same = (t: number, v: number) => t != null && Math.abs(t - v) <= tol;

    return EventRangeUtils.movedEnds(before, {
      value: same(before?.value, lo) ? before.value : lo,
      highValue: same(before?.highValue, hi) ? before.highValue : hi
    }, sorted);
  }

  /** Offset de la zona en un instante, en minutos y con el signo de Date.getTimezoneOffset. Inyectable en los tests. */
  static readonly localOffset: OffsetAt = (ms: number) => new Date(ms).getTimezoneOffset();

  /**
   * Reloj local de un instante expresado como si fuera UTC ("10:30 en Madrid" -> 10:30Z). Es lo que
   * recibe el back de un evento (contrato actual, que no se cambia aqui). Antes se hacia con
   * setMinutes(getMinutes() - getTimezoneOffset()), que cerca de un cambio de hora cae en el otro
   * offset y manda una hora de mas (25/10/2026 01:30 en Madrid salia 02:30Z).
   */
  static wallClockUtc(ms: number, offsetAt: OffsetAt = EventRangeUtils.localOffset): number {
    return ms - offsetAt(ms) * 60000;
  }

  /**
   * Instantes cuyo reloj local es `wall` (ver wallClockUtc), de menor a mayor: ninguno si esa hora
   * no existe (hueco al adelantar el reloj), dos si se repite (al atrasarlo). Se prueban los offsets
   * reales de la zona en +-27 h, asi que vale para cualquier tamano de transicion (30 min en Lord
   * Howe, 1 h en Madrid, 2 h en Troll).
   */
  static instantsOfWallClock(wall: number, offsetAt: OffsetAt = EventRangeUtils.localOffset): number[] {
    const offsets = new Set<number>();
    for (let t = wall - 27 * 3600000; t <= wall + 27 * 3600000; t += 15 * 60000) {
      offsets.add(offsetAt(t));
    }

    const instants = new Set<number>();
    offsets.forEach(o => {
      const ms = wall + o * 60000;
      if (EventRangeUtils.wallClockUtc(ms, offsetAt) === wall) {
        instants.add(ms);
      }
    });

    return Array.from(instants).sort((x, y) => x - y);
  }

  /**
   * Problema de una hora local tecleada en un cambio de hora: 'ambiguous' si ese reloj existe dos
   * veces (otono, p. ej. las dos 02:30 del 25/10/2026 en Madrid), 'nonexistent' si no existe
   * (primavera: luxon la pasaria a la hora siguiente sin avisar). null si es una hora normal o si
   * el texto no es una fecha.
   */
  static localTimeIssue(text: string, offsetAt: OffsetAt = EventRangeUtils.localOffset): 'ambiguous' | 'nonexistent' {
    if (SensorGraphUtils.parseFieldDate(text) == null) {
      return null;
    }

    const m = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})\s*$/.exec(text);
    if (m == null) {
      return null;
    }

    const [d, mo, y, h, mi, se] = m.slice(1).map(Number);
    const n = EventRangeUtils.instantsOfWallClock(Date.UTC(y, mo - 1, d, h, mi, se), offsetAt).length;

    return n === 0 ? 'nonexistent' : (n > 1 ? 'ambiguous' : null);
  }

  /**
   * Instante de un campo de texto. Si el texto no se ha tocado (sigue siendo el de `current`) es
   * `current`, el instante elegido con la franja o el slider: volver a leer el texto perderia cual
   * de las dos horas repetidas era. Si se ha tocado, se lee; y si la hora tecleada es ambigua o no
   * existe no se adivina: `issue` lo dice y `date` es null.
   */
  static fieldInstant(text: string, current: Date): { date: Date, issue: 'ambiguous' | 'nonexistent' } {
    const date = SensorGraphUtils.resolveFieldDate(text, current);

    if (date == null || date === current) {
      return { date, issue: null };
    }

    const issue = EventRangeUtils.localTimeIssue(text);

    return issue != null ? { date: null, issue } : { date, issue: null };
  }

  /**
   * Franja que dicen los campos de texto, para mover el slider; null si no debe moverse. Fuera de
   * la escala o invertida, ngx-slider la recortaria por su cuenta y los marcadores dejarian de
   * coincidir con los campos: el slider se queda donde estaba y manda el texto (el guardado
   * valida el orden). `current` es la franja actual: el campo que no se ha tocado conserva su
   * instante (ver fieldInstant).
   */
  static fromFields(startStr: string, endStr: string, floor: number, ceil: number, current?: DateRange): DateRange {
    const start = EventRangeUtils.fieldInstant(startStr, current?.start).date;
    const end = EventRangeUtils.fieldInstant(endStr, current?.end).date;

    const inScale = (d: Date) => floor == null || ceil == null || (d.getTime() >= floor && d.getTime() <= ceil);

    if (start == null || end == null || start.getTime() > end.getTime() || !inScale(start) || !inScale(end)) {
      return null;
    }

    return { start, end };
  }
}
